import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const CitiesContext = createContext(null);
const API_BASE = process.env.REACT_APP_API_BASE || "";

export function CitiesProvider({ children }) {
  const [cities, setCities] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(true);
  const [citiesError, setCitiesError] = useState("");
  const requestIdRef = useRef(0);

  const refetchCities = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsCitiesLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/cities`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Не удалось загрузить города (HTTP ${response.status})`);
      const data = await response.json();
      if (requestId !== requestIdRef.current) return;
      const items = (Array.isArray(data?.items) ? data.items : [])
        .map((city) => ({ ...city, key: String(city.key || "").trim().toLowerCase() }))
        .filter((city) => city.isActive !== false)
        .sort((a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          String(a.name || "").localeCompare(String(b.name || ""), "ru")
        );
      setCities(items); setCitiesError("");
    } catch (error) {
      if (requestId === requestIdRef.current) setCitiesError(error instanceof Error ? error.message : "Не удалось загрузить города");
    } finally { if (requestId === requestIdRef.current) setIsCitiesLoading(false); }
  }, []);

  useEffect(() => { refetchCities(); return () => { requestIdRef.current += 1; }; }, [refetchCities]);
  const cityByKey = useMemo(() => Object.fromEntries(cities.map((city) => [city.key, city])), [cities]);
  const cityNameByKey = useMemo(() => Object.fromEntries(cities.map((city) => [city.key, city.name])), [cities]);
  const value = useMemo(() => ({ cities, isCitiesLoading, citiesError, refetchCities, cityByKey, cityNameByKey }), [cities, isCitiesLoading, citiesError, refetchCities, cityByKey, cityNameByKey]);
  return <CitiesContext.Provider value={value}>{children}</CitiesContext.Provider>;
}

export function useCities() {
  const value = useContext(CitiesContext);
  if (!value) throw new Error("useCities must be used within CitiesProvider");
  return value;
}
